/**
 * Domínio MPA centrado em AÇÕES (GUPM-193) — TS puro, sem React/Supabase/DOM.
 * Fiel ao `_renderMPAList` do protótipo V8: achata as ações de todos os planos
 * numa carteira única, conta status globais (incl. vencidas derivadas) e filtra.
 * `hoje` é sempre injetado (nunca `new Date()` interno) → determinístico e testável.
 *
 * GUPM-332: a carteira segue achatada aqui; a tela a exibe agrupada por plano
 * (`agruparAcoesPorPlano`) e separa os planos 100% concluídos.
 *
 * SEM PII: `PAPEIS_EXECUCAO`/`SETORES` são papéis/categorias, nunca pessoas.
 */
import { diaEmBrasilia, somarDiasAoDia } from '../fuso-brasilia';
import { formatarNumeroTarp } from '../tarp/numero';
import type { AcaoStatus, Plano } from './index';

/** Ação achatada: os campos da ação + o contexto herdado do plano de origem. */
export interface AcaoFlat {
  planoId: string;
  codigo: string;
  damCode: string;
  nivel: string | null;
  tarpCode: string | null;
  tarpRecordId: string | null;
  tarpSeq: number | null;
  /** Última alteração do plano de origem (`updatedAt ?? createdAt`) — fallback da ordem dos concluídos. */
  planoAlteradoEm: string;
  /** Criação do plano de origem (timestamptz). */
  planoCriadoEm: string;
  /** Conclusão desta ação, carimbada pelo banco; `null` se não concluída ou sem registro. */
  concluidoEm: string | null;
  instrumento: string | null;
  id: string;
  descricao: string;
  responsavel: string;
  prazo: string | null;
  status: AcaoStatus;
}

/** Filtros da carteira (fiel aos 5 grupos do V8). `Todas`/`Todos` = sem filtro. */
export interface MpaFiltros {
  dam: string; // 'Todas' | 'A' | 'B' | 'C'
  status: string; // 'Todas' | AcaoStatus
  nivel: string; // 'Todos' | 'ATENCAO' | 'ALERTA' | 'EMERGENCIA'
  resp: string; // 'Todos' | <responsavel>
  prazo: string; // 'Todos' | 'Vencido' | 'Esta semana' | 'Sem prazo'
}

/** Contagem global (não-filtrada) das 5 pills. */
export interface ContagemGlobal {
  total: number;
  pendentes: number;
  andamento: number;
  concluidas: number;
  vencidas: number;
}

/** Resultado do cálculo de prazo em dias úteis. */
export type PrazoResultado =
  | { invalido: true }
  | { invalido: false; dias: number; faixa: 'curto' | 'medio' | 'longo' };

/**
 * Dia de calendário (YYYY-MM-DD) de `hoje` em BRASÍLIA — base das comparações de prazo.
 * O prazo é o dia da barragem; cortar em UTC (`toISOString`) fazia o prazo de hoje
 * vencer às 21h daqui (GUPM-332).
 */
function ymd(d: Date): string {
  return diaEmBrasilia(d);
}

/** `Date` UTC-meia-noite a partir de uma data ISO (YYYY-MM-DD). Determinístico, sem fuso local. */
function parseYmd(iso: string): Date {
  return new Date(`${iso}T00:00:00.000Z`);
}

/**
 * Uma ação está VENCIDA quando tem prazo no passado e ainda não foi concluída
 * (`prazo < hoje && status ≠ concluido`). Regra derivada — nunca status armazenado
 * (regra do card). Comparação por data ISO (string), estável em qualquer fuso.
 */
export function acaoVencida(acao: Pick<AcaoFlat, 'prazo' | 'status'>, hoje: Date): boolean {
  if (acao.status === 'concluido' || !acao.prazo) return false;
  return acao.prazo < ymd(hoje);
}

/** Achata as ações de todos os planos, carregando o contexto do plano em cada ação. */
export function flattenAcoes(planos: Plano[]): AcaoFlat[] {
  return planos.flatMap((p) =>
    p.acoes.map((a) => ({
      planoId: p.id,
      codigo: p.codigo,
      damCode: p.damCode,
      nivel: p.nivel,
      tarpCode: p.tarpCode,
      tarpRecordId: p.tarpRecordId,
      tarpSeq: p.tarpSeq,
      planoAlteradoEm: p.updatedAt ?? p.createdAt,
      planoCriadoEm: p.createdAt,
      concluidoEm: a.status === 'concluido' ? (a.concluidoEm ?? null) : null,
      instrumento: p.instrumento ?? null,
      id: a.id,
      descricao: a.descricao,
      responsavel: a.responsavel,
      prazo: a.prazo,
      status: a.status,
    })),
  );
}

/** Conta as ações por status + vencidas (`prazo < hoje && status ≠ concluido`). */
export function contarAcoesGlobais(acoes: AcaoFlat[], hoje: Date): ContagemGlobal {
  const c: ContagemGlobal = { total: acoes.length, pendentes: 0, andamento: 0, concluidas: 0, vencidas: 0 };
  for (const a of acoes) {
    if (a.status === 'pendente') c.pendentes++;
    else if (a.status === 'andamento') c.andamento++;
    else if (a.status === 'concluido') c.concluidas++;
    if (acaoVencida(a, hoje)) c.vencidas++;
  }
  return c;
}

/** Aplica os 5 filtros (AND). Filtros de prazo usam `hoje` como referência. */
export function filtrarAcoes(acoes: AcaoFlat[], filtros: MpaFiltros, hoje: Date): AcaoFlat[] {
  const inicioSemana = ymd(hoje);
  const fimSemana = somarDiasAoDia(inicioSemana, 7);
  return acoes.filter((a) => {
    if (filtros.dam !== 'Todas' && a.damCode !== filtros.dam) return false;
    if (filtros.status !== 'Todas' && a.status !== filtros.status) return false;
    if (filtros.nivel !== 'Todos' && a.nivel !== filtros.nivel) return false;
    if (filtros.resp !== 'Todos' && a.responsavel !== filtros.resp) return false;
    switch (filtros.prazo) {
      case 'Vencido':
        return acaoVencida(a, hoje);
      case 'Sem prazo':
        return a.prazo === null;
      case 'Esta semana':
        return a.prazo !== null && a.prazo >= inicioSemana && a.prazo <= fimSemana;
      default: // 'Todos'
        return true;
    }
  });
}

/**
 * Um PLANO com as ações que sobreviveram aos filtros (GUPM-332). A carteira
 * continua achatada em ações; o agrupamento é só a forma de exibi-la — o
 * contexto herdado do plano (barragem, nível, TARP, instrumento) sobe para o
 * grupo e deixa de ser repetido em cada ação.
 */
export interface GrupoPlano {
  planoId: string;
  codigo: string;
  damCode: string;
  nivel: string | null;
  tarpCode: string | null;
  tarpRecordId: string | null;
  tarpSeq: number | null;
  /**
   * Última alteração do plano (`updatedAt ?? createdAt`). Só FALLBACK da ordem dos
   * concluídos, para plano sem `concluidoEm` registrado.
   */
  alteradoEm: string;
  /** Criação do plano (timestamptz) — exibida no cabeçalho do card. */
  criadoEm: string;
  /**
   * Conclusão da ÚLTIMA ação concluída do plano (plano inteiro, ignora filtros) —
   * num plano 100% concluído, é quando ele foi concluído. `null` sem registro.
   */
  concluidoEm: string | null;
  instrumento: string | null;
  /** Ações VISÍVEIS (pós-filtro), na ordem original da carteira. */
  acoes: AcaoFlat[];
  /** Total de ações do plano, ignorando filtros. */
  totalAcoes: number;
  /** Concluídas no plano, ignorando filtros. */
  concluidasTotal: number;
  /** Plano 100% concluído — base da partição da tela. */
  todasConcluidas: boolean;
}

/**
 * Qual TARP abriu o plano, como o usuário a encontra na lista do M3 (`TR-nnnn`).
 * `tarpCode` sozinho não serve: é o protocolo do catálogo, repetido por toda TARP
 * do mesmo tipo. Os três casos são distintos de propósito — um vínculo que não
 * carregou NÃO pode ser apresentado como "sem TARP".
 */
export function rotuloTarpDeOrigem(p: { tarpRecordId: string | null; tarpSeq: number | null }): string {
  if (p.tarpSeq != null) return formatarNumeroTarp(p.tarpSeq);
  return p.tarpRecordId ? 'TARP não identificada' : 'Sem TARP vinculada';
}

/** Conta ações e concluídas por plano — base do progresso exibido no cabeçalho. */
interface TotaisDoPlano {
  total: number;
  concluidas: number;
  /** Conclusão mais recente entre as ações (ISO UTC ordena como texto). */
  ultimaConclusao: string | null;
}

function contarPorPlano(acoes: AcaoFlat[]): Map<string, TotaisDoPlano> {
  const m = new Map<string, TotaisDoPlano>();
  for (const a of acoes) {
    const t = m.get(a.planoId) ?? { total: 0, concluidas: 0, ultimaConclusao: null };
    t.total++;
    if (a.status === 'concluido') t.concluidas++;
    if (a.concluidoEm && (!t.ultimaConclusao || a.concluidoEm > t.ultimaConclusao)) t.ultimaConclusao = a.concluidoEm;
    m.set(a.planoId, t);
  }
  return m;
}

/**
 * Agrupa a carteira achatada por plano, preservando a ordem de PRIMEIRA aparição
 * de cada plano e a ordem das ações dentro dele.
 *
 * `carteiraCompleta` é a carteira ANTES dos filtros e serve só ao progresso do
 * cabeçalho: contar apenas as ações que passaram no filtro faria o mesmo plano
 * exibir "1/1 concluídas" sob um filtro e "1/3" sob outro — um número que mente.
 * Um plano sem nenhuma ação visível não vira grupo.
 */
export function agruparAcoesPorPlano(visiveis: AcaoFlat[], carteiraCompleta: AcaoFlat[]): GrupoPlano[] {
  const totais = contarPorPlano(carteiraCompleta);
  // Fail-safe do contrato: espera-se que todo plano de `visiveis` exista em
  // `carteiraCompleta` (hoje a UI deriva uma da outra). Se um chamador futuro
  // quebrar isso, o progresso cai para as próprias ações visíveis em vez de
  // exibir "0/0" — um número que mentiria no cabeçalho. O grupo nunca é
  // descartado: numa tela de segurança de barragem, esconder uma ação é pior
  // do que exibi-la com contagem parcial.
  const totaisVisiveis = contarPorPlano(visiveis);

  // `Map` preserva a ordem de inserção → ordem de primeira aparição do plano.
  const porPlano = new Map<string, GrupoPlano>();
  for (const a of visiveis) {
    const grupo = porPlano.get(a.planoId);
    if (grupo) {
      grupo.acoes.push(a);
      continue;
    }
    const t = totais.get(a.planoId) ?? totaisVisiveis.get(a.planoId) ?? { total: 0, concluidas: 0, ultimaConclusao: null };
    porPlano.set(a.planoId, {
      planoId: a.planoId,
      codigo: a.codigo,
      damCode: a.damCode,
      nivel: a.nivel,
      tarpCode: a.tarpCode,
      tarpRecordId: a.tarpRecordId,
      tarpSeq: a.tarpSeq,
      alteradoEm: a.planoAlteradoEm,
      criadoEm: a.planoCriadoEm,
      concluidoEm: t.ultimaConclusao,
      instrumento: a.instrumento,
      acoes: [a],
      totalAcoes: t.total,
      concluidasTotal: t.concluidas,
      todasConcluidas: t.total > 0 && t.concluidas === t.total,
    });
  }
  return [...porPlano.values()];
}

/**
 * Separa os grupos entre a carteira ativa e os planos já encerrados (GUPM-332).
 * Só o plano INTEIRAMENTE concluído sai da lista principal — um plano misto
 * fica, íntegro, onde o trabalho ainda acontece.
 *
 * Os concluídos saem da conclusão MAIS RECENTE para a mais antiga (decisão do dono,
 * 26/09); os ativos mantêm a ordem da carteira. A chave é a conclusão da última ação
 * (carimbada pelo banco); sem registro, cai para a última alteração do plano.
 * Compara INSTANTES, não texto: o banco devolve `…Z` e o PostgREST `…+00:00`.
 */
export function particionarGruposPorConclusao(grupos: GrupoPlano[]): {
  ativos: GrupoPlano[];
  concluidos: GrupoPlano[];
} {
  const ativos: GrupoPlano[] = [];
  const concluidos: GrupoPlano[] = [];
  for (const g of grupos) (g.todasConcluidas ? concluidos : ativos).push(g);
  const chave = (g: GrupoPlano) => Date.parse(g.concluidoEm ?? g.alteradoEm);
  concluidos.sort((a, b) => chave(b) - chave(a));
  return { ativos, concluidos };
}

/**
 * Grupos que casam com a barragem e o nível escolhidos na toolbar — os dois filtros
 * que descrevem o PLANO (status, responsável e prazo descrevem a ação). Base da
 * pista do vazio da lista principal: sem isto, "Barragem A" anunciava concluídos
 * da B, que o filtro ativo exclui.
 */
export function gruposQueCasamComContexto(
  grupos: GrupoPlano[],
  filtros: Pick<MpaFiltros, 'dam' | 'nivel'>,
): GrupoPlano[] {
  return grupos.filter(
    (g) =>
      (filtros.dam === 'Todas' || g.damCode === filtros.dam) &&
      (filtros.nivel === 'Todos' || g.nivel === filtros.nivel),
  );
}

/** Ações de um card, separadas entre o que fica à vista e o que fica recolhido. */
export interface AcoesDoCard {
  /** Sempre visíveis. */
  ativas: AcaoFlat[];
  /** Recolhidas atrás do toggle do card. */
  recolhiveis: AcaoFlat[];
}

/**
 * Dentro de um plano misto, as ações já concluídas ficam recolhidas — elas são o
 * volume morto que polui o card (GUPM-332).
 *
 * A exceção importa: sob um filtro como `status=concluido`, as únicas ações
 * visíveis do plano SÃO as concluídas. Recolhê-las ali faria o card nascer
 * visualmente vazio, então nesse caso elas próprias são o conteúdo.
 */
export function separarConcluidasDoGrupo(grupo: GrupoPlano): AcoesDoCard {
  const ativas = grupo.acoes.filter((a) => a.status !== 'concluido');
  if (ativas.length === 0) return { ativas: grupo.acoes, recolhiveis: [] };
  return { ativas, recolhiveis: grupo.acoes.filter((a) => a.status === 'concluido') };
}

/**
 * Conta dias úteis (seg–sex) entre abertura e conclusão (fiel ao `_naCalcPrazo` do V8):
 * conta os dias úteis do dia SEGUINTE à abertura até a conclusão (inclusive).
 * Conclusão ≤ abertura → `{ invalido: true }` (não calcula prazo negativo/zero).
 * Faixa de cor por corte: ≤3 `curto` (vermelho) · ≤7 `medio` (amarelo) · >7 `longo` (verde).
 */
export function prazoDiasUteis(abertura: string, conclusao: string): PrazoResultado {
  const ini = parseYmd(abertura);
  const fim = parseYmd(conclusao);
  if (!(fim.getTime() > ini.getTime())) return { invalido: true };
  let dias = 0;
  const cur = new Date(ini);
  cur.setUTCDate(cur.getUTCDate() + 1);
  while (cur.getTime() <= fim.getTime()) {
    const wd = cur.getUTCDay(); // 0 = domingo, 6 = sábado
    if (wd !== 0 && wd !== 6) dias++;
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  const faixa = dias <= 3 ? 'curto' : dias <= 7 ? 'medio' : 'longo';
  return { invalido: false, dias, faixa };
}

/**
 * Papéis/partes de execução — SEM PII (papéis, não pessoas). Sanitizado do V8:
 * os nomes reais do protótipo (Giorgi, Rodolfo, …) viram PAPEL institucional.
 */
export const PAPEIS_EXECUCAO: string[] = [
  'RT Foscentro',
  'EoR Walm',
  'JF Brasil',
  'Coord. PAEBM',
  'Vigilante',
  'TetraTech',
  'TECAL',
  'TI Foscentro',
  'Projetista',
];

/** Setores responsáveis — categorias fixas, sem PII. */
export const SETORES: string[] = [
  'Geotecnia',
  'Operação',
  'Manutenção',
  'Meio Ambiente',
  'Segurança de Barragem',
  'Gestão / Diretoria',
];
